import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../../store/authStore";
import Spinner from "../../../Spinner/Spinner";
import "./NotificationsModal.scss";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5001";

// Helper to normalize color
const normalizeColor = (color) => {
  if (!color) return "inherit";
  if (color.startsWith("#")) return color;
  return color;
};

// Helper to format deadline
const formatDeadline = (deadline) => {
  if (!deadline) return "";
  const date = new Date(deadline);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);

  const diffTime = deadlineDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""} overdue`;
  }
  if (diffDays === 0) return "Due today";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

function NotificationsModal({ onClose }) {
  const [urgentTasks, setUrgentTasks] = useState({ overdue: [], dueToday: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clickedItemId, setClickedItemId] = useState(null);
  const isProcessingRef = useRef(false);
  const navigate = useNavigate();

  const { token, user, setCurrentProject, fetchProjects } = useAuthStore();

  // Fetch urgent tasks on mount
  const fetchUrgentTasks = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE}/api/search/urgent`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch urgent tasks");
      }

      const data = await res.json();
      setUrgentTasks(data);
    } catch (err) {
      console.error("Error fetching urgent tasks:", err);
      setError("Failed to load notifications");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUrgentTasks();
  }, [fetchUrgentTasks]);

  // Handle item click - switch org/project and navigate
  const handleItemClick = useCallback(
    async (item) => {
      // Prevent multiple clicks using ref for immediate check
      if (isProcessingRef.current) {
        return;
      }

      setClickedItemId(item.id);
      isProcessingRef.current = true;

      try {
        // Check if it's a personal list item (no organization)
        const isPersonalList = item.type === "listItem" && !item.organization;

        if (!isPersonalList && item.organization?.id) {
          // Switch to the target organization
          const orgResponse = await fetch(
            `${API_BASE}/api/auth/switch-organization`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ organizationId: item.organization.id }),
            },
          );

          if (orgResponse.ok) {
            const orgData = await orgResponse.json();
            const { user: updatedUser, currentProject } = orgData;

            // Update the store with new user and currentProject
            useAuthStore.setState({
              user: updatedUser,
              currentProject: currentProject,
            });

            // Fetch projects for the new organization and wait for it to complete
            await fetchProjects();
          }

          // Set the specific project if different from what the org switch set
          if (item.project?.id) {
            // Fetch the full project object from the store's projects array
            const projects = useAuthStore.getState().projects;
            const fullProject = projects.find((p) => p._id === item.project.id);
            if (fullProject) {
              setCurrentProject(fullProject);
            }
          }

          // Small delay for state to propagate
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Navigate based on item type
        if (item.type === "listItem") {
          // Navigate to the appropriate list page with selectList parameter
          if (isPersonalList) {
            navigate(`/personal-lists?selectList=${item.listId}`);
          } else {
            navigate(`/project-lists?selectList=${item.listId}`);
          }
        } else {
          // Navigate to tasks page with selectTask parameter
          navigate(`/boards?selectTask=${item.id}`);
        }

        // Close the modal
        if (onClose) {
          onClose();
        }

        // Reset clicked state after modal closes
        setClickedItemId(null);
        isProcessingRef.current = false;
      } catch (error) {
        console.error("Error navigating to item:", error);
        setClickedItemId(null); // Reset on error
        isProcessingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, token, navigate, onClose, fetchProjects, setCurrentProject],
  );

  const totalCount = urgentTasks.overdue.length + urgentTasks.dueToday.length;

  return (
    <div className="notifications-modal">
      <div className="notifications-modal__header">
        <span className="material-icons notifications-modal__header-icon">
          notifications_active
        </span>
        <span className="notifications-modal__header-title">Urgent Tasks</span>
        {totalCount > 0 && (
          <span className="notifications-modal__badge">{totalCount}</span>
        )}
      </div>

      <div className="notifications-modal__content">
        {isLoading ? (
          <div className="notifications-modal__loading">
            <Spinner />
          </div>
        ) : error ? (
          <div className="notifications-modal__error">
            <span className="material-icons">error_outline</span>
            <p>{error}</p>
            <button onClick={fetchUrgentTasks}>Retry</button>
          </div>
        ) : totalCount === 0 ? (
          <div className="notifications-modal__empty">
            <span className="material-icons">check_circle</span>
            <p>No urgent tasks!</p>
            <span className="notifications-modal__empty-subtitle">
              All caught up
            </span>
          </div>
        ) : (
          <>
            {/* Overdue Tasks */}
            {urgentTasks.overdue.length > 0 && (
              <div className="notifications-modal__section">
                <h3 className="notifications-modal__section-title notifications-modal__section-title--overdue">
                  <span className="material-icons">warning</span>
                  Overdue ({urgentTasks.overdue.length})
                </h3>
                <div className="notifications-modal__list">
                  {urgentTasks.overdue.map((item) => (
                    <div
                      key={item.id}
                      className="notifications-modal__item notifications-modal__item--overdue"
                      onClick={() => handleItemClick(item)}
                      style={{
                        opacity: clickedItemId === item.id ? 0.6 : 1,
                        pointerEvents: clickedItemId ? "none" : "auto",
                      }}
                    >
                      {clickedItemId === item.id && (
                        <div className="notifications-modal__item-loading">
                          <div className="notifications-modal__spinner"></div>
                        </div>
                      )}
                      <div className="notifications-modal__item-header">
                        <span className="notifications-modal__item-title">
                          {item.title}
                        </span>
                        <span className="notifications-modal__item-deadline">
                          {formatDeadline(item.deadline)}
                        </span>
                      </div>
                      <div className="notifications-modal__item-type-label">
                        {item.type === "listItem" ? "List item" : "Task"}
                      </div>
                      <div className="notifications-modal__item-meta">
                        {item.type === "listItem" && !item.organization ? (
                          // Personal list item
                          <span className="notifications-modal__meta-item">
                            <span className="material-icons">person</span>
                            Personal List - {item.listName || "Unknown"}
                          </span>
                        ) : (
                          // Task or project list item
                          <>
                            <span
                              className="notifications-modal__meta-item"
                              style={{
                                color: normalizeColor(item.organization?.color),
                              }}
                            >
                              <span className="material-icons">business</span>
                              {item.organization?.name || "Unknown"}
                            </span>
                            <span
                              className="notifications-modal__meta-item"
                              style={{
                                color: normalizeColor(item.project?.color),
                              }}
                            >
                              <span className="material-icons">folder</span>
                              {item.project?.name || "Unknown"}
                            </span>
                            {item.type === "listItem" && item.listName && (
                              <span className="notifications-modal__meta-item">
                                <span className="material-icons">list</span>
                                {item.listName}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Due Today Tasks */}
            {urgentTasks.dueToday.length > 0 && (
              <div className="notifications-modal__section">
                <h3 className="notifications-modal__section-title notifications-modal__section-title--today">
                  <span className="material-icons">today</span>
                  Due Today ({urgentTasks.dueToday.length})
                </h3>
                <div className="notifications-modal__list">
                  {urgentTasks.dueToday.map((item) => (
                    <div
                      key={item.id}
                      className="notifications-modal__item notifications-modal__item--today"
                      onClick={() => handleItemClick(item)}
                      style={{
                        opacity: clickedItemId === item.id ? 0.6 : 1,
                        pointerEvents: clickedItemId ? "none" : "auto",
                      }}
                    >
                      {clickedItemId === item.id && (
                        <div className="notifications-modal__item-loading">
                          <div className="notifications-modal__spinner"></div>
                        </div>
                      )}
                      <div className="notifications-modal__item-header">
                        <span className="notifications-modal__item-title">
                          {item.title}
                        </span>
                        <span className="notifications-modal__item-deadline notifications-modal__item-deadline--today">
                          Due today
                        </span>
                      </div>
                      <div className="notifications-modal__item-type-label">
                        {item.type === "listItem" ? "List item" : "Task"}
                      </div>
                      <div className="notifications-modal__item-meta">
                        {item.type === "listItem" && !item.organization ? (
                          // Personal list item
                          <span className="notifications-modal__meta-item">
                            <span className="material-icons">person</span>
                            Personal List - {item.listName || "Unknown"}
                          </span>
                        ) : (
                          // Task or project list item
                          <>
                            <span
                              className="notifications-modal__meta-item"
                              style={{
                                color: normalizeColor(item.organization?.color),
                              }}
                            >
                              <span className="material-icons">business</span>
                              {item.organization?.name || "Unknown"}
                            </span>
                            <span
                              className="notifications-modal__meta-item"
                              style={{
                                color: normalizeColor(item.project?.color),
                              }}
                            >
                              <span className="material-icons">folder</span>
                              {item.project?.name || "Unknown"}
                            </span>
                            {item.type === "listItem" && item.listName && (
                              <span className="notifications-modal__meta-item">
                                <span className="material-icons">list</span>
                                {item.listName}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default NotificationsModal;
